import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useSpatialNavigation } from "../hooks/useSpatialNavigation";
import { DistributionClient } from "../lib/api";
import { useAuth } from "../lib/authContext";
import { formatDuration } from "../lib/format";
import { isSummary, type BrowseMovie } from "../lib/movie";
import {
  hasPlayableAsset,
  isImmediatePlayerRelease,
  normalizeCatalog,
} from "../lib/playback";
import { releaseKey } from "../lib/release";
import { progressStore, settingsStore, watchlistStore } from "../lib/storage";
import { translate } from "../lib/i18n";
import { I18nProvider } from "../lib/i18n-context";
import { useI18n } from "../lib/use-i18n";
import type {
  ApiSettings,
  CatalogFilterSelection,
  CatalogResponse,
  DiscoveryMovie,
  DiscoveryResponse,
  MovieDetail,
  MovieSummary,
  Release,
  WatchProgress,
  FrontendFeatures,
} from "../lib/types";
import { CatalogFilters } from "./CatalogFilters";
import { EMPTY_CATALOG_FILTER, catalogFilterActive, matchesCatalogFilter } from "../lib/catalog-filter";
import { MovieCard, MovieRail } from "./MovieCard";
import { MovieDetailOverlay } from "./MovieDetailOverlay";
import { PlayerOverlay } from "./PlayerOverlay";
import { ReleasePicker, SearchPanel } from "./SearchPanel";
import { AccountView } from "./AccountView";
import { AuthView } from "./AuthView";
import { CyberTubeView } from "./CyberTubeView";
import { CyberTubeWatch } from "./CyberTubeWatch";
import { IptvView } from "./IptvView";
import { IptvWatch } from "./IptvWatch";
import { SeriesView } from './series/SeriesView';
import { parseSeriesSeasonQuery } from '../lib/series-search';
import {
  cybertubeProgressMovie,
  cybertubeWatchProgress,
} from "../lib/cybertube";
import type { CybertubeProgress } from "../lib/types";

type View = "home" | "search" | "series" | "cybertube" | "iptv" | "library" | "account";

const DEFAULT_FRONTEND_FEATURES: FrontendFeatures = { iptv: true, cybertube: true };

function emptyCatalog(locale: ApiSettings["locale"]): CatalogResponse {
  return {
    locale,
    count: 0,
    available: { count: 0, items: [] },
    upcoming: { count: 0, items: [] },
    items: [],
  };
}

function emptyDiscovery(locale: ApiSettings["locale"]): DiscoveryResponse {
  return { locale, type: "trending", count: 0, items: [] };
}

function discoveryDetail(movie: DiscoveryMovie): MovieDetail {
  return {
    id: movie.movieId ?? movie.tmdbId,
    slug: null,
    title: movie.title,
    year: movie.year,
    poster: movie.poster,
    backdrop: movie.backdrop,
    description: movie.description,
    durationSec: null,
    runtime: null,
    video: null,
    playback: null,
    href: movie.href ?? "",
    availability: {
      status: "pending",
      label: movie.inLibrary ? "In library" : "Available to request",
      progress: 0,
      playbackReady: false,
      availableAt: null,
      availableInSec: null,
    },
    audio: [],
    subtitles: [],
    tmdbId: movie.tmdbId,
    type: movie.type === "series" ? "series" : "movie",
  };
}

function summaryDetail(movie: MovieSummary): MovieDetail {
  return { ...movie, audio: [], subtitles: [] };
}

function watchlistId(
  movie: Pick<MovieSummary, "id" | "type" | "tmdbId">,
): string {
  return movie.type === "series" && movie.tmdbId
    ? `series:${movie.tmdbId.replace(/^tv-series:/, "")}`
    : movie.id;
}

async function waitForWebPlayback(
  client: DistributionClient,
  movieId: string,
  cancelled: () => boolean,
  onStatus: (movie: MovieDetail) => void,
): Promise<MovieDetail | null> {
  const deadline = Date.now() + 180_000;
  while (!cancelled() && Date.now() < deadline) {
    try {
      const movie = await client.status(movieId);
      onStatus(movie);
      if (hasPlayableAsset(movie)) return movie;
      if (movie.availability.status === "failed") return null;
    } catch {
      // The movie exists before its first playable fragment; keep polling.
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1_200));
  }
  return null;
}

export function CyberMoviesApp() {
  const auth = useAuth();
  const [settings, setSettings] = useState<ApiSettings>(() =>
    settingsStore.read(),
  );
  const t = useMemo(() => translate(settings.locale), [settings.locale]);
  const [catalog, setCatalog] = useState<CatalogResponse>(() =>
    emptyCatalog(settings.locale),
  );
  const [trending, setTrending] = useState<DiscoveryResponse>(() =>
    emptyDiscovery(settings.locale),
  );
  const [view, setView] = useState<View>("home");
  const [features, setFeatures] = useState<FrontendFeatures>(DEFAULT_FRONTEND_FEATURES);
  const [seriesTmdbId, setSeriesTmdbId] = useState<string | null>(null);
  const [seriesUnread, setSeriesUnread] = useState(0);
  const [seriesHandlesBack, setSeriesHandlesBack] = useState(false);
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const [seriesSeasonNumber, setSeriesSeasonNumber] = useState<number | null>(null);
  const [knownSeries, setKnownSeries] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connection, setConnection] = useState(() =>
    translate(settings.locale)("loadingCatalog"),
  );
  const [query, setQuery] = useState("");
  const [metadataResults, setMetadataResults] = useState<DiscoveryMovie[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState<MovieDetail | null>(null);
  const [selectedMovieId, setSelectedMovieId] = useState<string | null>(null);
  const [playerMovie, setPlayerMovie] = useState<MovieDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [watchlist, setWatchlist] = useState<string[]>(() =>
    watchlistStore.list(),
  );
  const [savedTitles, setSavedTitles] = useState<MovieSummary[]>(() =>
    watchlistStore.items(),
  );
  const [progress, setProgress] = useState<WatchProgress[]>([]);
  const [cybertubeProgress, setCybertubeProgress] = useState<
    CybertubeProgress[]
  >([]);
  const [cybertubeSource, setCybertubeSource] = useState<string | null>(null);
  const [iptvChannel, setIptvChannel] = useState<string | null>(null);
  const [releaseMovie, setReleaseMovie] = useState<DiscoveryMovie | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [acquiringReleaseKey, setAcquiringReleaseKey] = useState<string | null>(
    null,
  );
  const [acquireProgress, setAcquireProgress] = useState<number | null>(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const [previousHero, setPreviousHero] = useState<MovieSummary | null>(null);
  const [heroDirection, setHeroDirection] = useState<1 | -1>(1);
  const [heroPaused, setHeroPaused] = useState(false);
  const [toast, setToast] = useState("");
  const searchVersion = useRef(0);
  const detailVersion = useRef(0);
  const searchInput = useRef<HTMLInputElement>(null);
  const progressSync = useRef<Promise<void>>(Promise.resolve());
  const acquireVersion = useRef(0);
  const heroTransitionTimer = useRef<number | null>(null);

  useSpatialNavigation(true);
  const client = useMemo(
    () =>
      auth.session ? new DistributionClient(settings, auth.session) : null,
    [auth.session, settings],
  );

  // Unread series notifications on the dock (§2.3); the series section reports
  // its own changes at once, this keeps the count fresh elsewhere.
  useEffect(() => {
    if (!client) return;
    let active = true;
    const load = () => void client.seriesUnreadCount().then((result) => { if (active) setSeriesUnread(result.count); }).catch(() => undefined);
    load();
    const timer = window.setInterval(load, 60_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [client]);

  useEffect(() => {
    if (!client) return;
    const ids = [...new Set(metadataResults.filter((item) => item.type === 'series')
      .map((item) => Number(item.tmdbId?.replace(/^tv-series:/, '')))
      .filter((id) => Number.isInteger(id) && id > 0))].slice(0, 50);
    if (!ids.length) return;
    let active = true;
    void client.seriesLookup(ids).then((result) => {
      if (active) setKnownSeries(Object.fromEntries(result.items.map((item) => [String(item.tmdbId), item.seriesId])));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [client, metadataResults]);

  useEffect(() => {
    document.documentElement.lang = settings.locale.toLowerCase();
    if (
      "serviceWorker" in navigator &&
      import.meta.env.PROD &&
      window.location.protocol.startsWith("http")
    ) {
      void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }
    const tizen = (
      window as typeof window & {
        tizen?: { tvinputdevice?: { registerKey: (key: string) => void } };
      }
    ).tizen;
    for (const key of ["MediaPlayPause", "MediaRewind", "MediaFastForward"]) {
      try {
        tizen?.tvinputdevice?.registerKey(key);
      } catch {
        /* Optional device API. */
      }
    }
  }, [settings.locale]);

  const loadData = useCallback(
    async (nextSettings = settings) => {
      const session = auth.session;
      if (!session) {
        setCatalog(emptyCatalog(nextSettings.locale));
        setTrending(emptyDiscovery(nextSettings.locale));
        setProgress([]);
        setCybertubeProgress([]);
        setFeatures(DEFAULT_FRONTEND_FEATURES);
        setConnected(false);
        setLoading(false);
        return false;
      }
      setLoading(true);
      try {
        const api = new DistributionClient(nextSettings, session);
        const frontendFeatures = await api.features().catch(() => DEFAULT_FRONTEND_FEATURES);
        setFeatures(frontendFeatures);
        const [
          catalogDataRaw,
          trendingData,
          remoteProgress,
          remoteCybertubeProgress,
        ] = await Promise.all([
          api.catalog(),
          api.trending().catch(() => emptyDiscovery(nextSettings.locale)),
          api.watchProgress().catch(() => progressStore.list(session.uid)),
          frontendFeatures.cybertube ? api.cybertubeProgress().catch(() => []) : Promise.resolve([]),
        ]);
        const catalogData = normalizeCatalog(catalogDataRaw);
        progressStore.replace(session.uid, remoteProgress);
        setCatalog(catalogData);
        setTrending(trendingData);
        setProgress(remoteProgress);
        setCybertubeProgress(remoteCybertubeProgress);
        setConnected(true);
        setConnection(t("onlineReady", { count: catalogData.available.count }));
        return true;
      } catch {
        setConnected(false);
        setConnection(t("tryAgainSoon"));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [auth.session, settings, t],
  );

  useEffect(() => {
    if ((view === 'cybertube' && !features.cybertube) || (view === 'iptv' && !features.iptv)) {
      setView('home');
      setQuery('');
    }
    if (!features.cybertube) setCybertubeSource(null);
    if (!features.iptv) setIptvChannel(null);
  }, [features, view]);

  const updateProgress = useCallback(
    (next: WatchProgress) => {
      if (!auth.session) return;
      progressStore.write(auth.session.uid, next);
      setProgress(progressStore.list(auth.session.uid));
      if (client) {
        progressSync.current = progressSync.current
          .catch(() => undefined)
          .then(() => client.saveWatchProgress(next))
          .then(
            () => undefined,
            () => undefined,
          );
      }
    },
    [auth.session, client],
  );

  const clearProgress = useCallback(
    (movieId: string) => {
      if (!auth.session) return;
      progressStore.remove(auth.session.uid, movieId);
      setProgress(progressStore.list(auth.session.uid));
      if (client) {
        progressSync.current = progressSync.current
          .catch(() => undefined)
          .then(() => client.clearWatchProgress(movieId))
          .then(
            () => undefined,
            () => undefined,
          );
      }
    },
    [auth.session, client],
  );

  const completeProgress = useCallback(
    (movieId: string, duration: number) => {
      if (!auth.session) return;
      progressStore.remove(auth.session.uid, movieId);
      setProgress(progressStore.list(auth.session.uid));
      if (client && duration > 0) {
        const completed: WatchProgress = {
          movieId,
          currentTime: duration,
          duration,
          updatedAt: Date.now(),
          completed: true,
        };
        progressSync.current = progressSync.current
          .catch(() => undefined)
          .then(() => client.saveWatchProgress(completed))
          .then(
            () => undefined,
            () => undefined,
          );
      }
    },
    [auth.session, client],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => void loadData(), 0);
    return () => window.clearTimeout(timer);
  }, [loadData]);

  useEffect(() => {
    if (!catalog.upcoming.count) return;
    const timer = window.setInterval(() => void loadData(), 15_000);
    return () => window.clearInterval(timer);
  }, [catalog.upcoming.count, loadData]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3_200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const onBack = (event: Event) => {
      if (playerMovie) return;
      // The series section closes its own player, dialog and pages first.
      if (view === 'series' && seriesHandlesBack) return;
      if (releaseMovie) {
        event.preventDefault();
        if (!acquiringReleaseKey) setReleaseMovie(null);
        return;
      }
      if (selectedMovie) {
        setSelectedMovie(null);
        setSelectedMovieId(null);
      } else if (view !== "home") setView("home");
      else return;
      event.preventDefault();
    };
    window.addEventListener("cyber-movies:back", onBack);
    return () => window.removeEventListener("cyber-movies:back", onBack);
  }, [acquiringReleaseKey, playerMovie, releaseMovie, selectedMovie, seriesHandlesBack, view]);

  useEffect(() => {
    if (view !== "search" || !client) return;
    const version = ++searchVersion.current;
    if (query.trim().length < 2) return;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const requestedSeason = parseSeriesSeasonQuery(query);
        const results = (await client.metadataSearch(requestedSeason?.title ?? query.trim())).items;
        if (searchVersion.current === version)
          setMetadataResults(results.filter((movie) => !movie.inLibrary && (!requestedSeason || movie.type === 'series')));
      } catch {
        if (searchVersion.current === version) setMetadataResults([]);
      } finally {
        if (searchVersion.current === version) setSearching(false);
      }
    }, 240);
    return () => window.clearTimeout(timer);
  }, [client, query, view]);

  const changeQuery = (nextQuery: string) => {
    setQuery(nextQuery);
    if (nextQuery.trim().length < 2) {
      searchVersion.current += 1;
      setMetadataResults([]);
      setSearching(false);
    }
  };

  const libraryResults = useMemo(() => {
    const requestedSeason = parseSeriesSeasonQuery(query);
    const needle = (requestedSeason?.title ?? query.trim()).toLocaleLowerCase();
    return needle
      ? catalog.items.filter((movie) => (!requestedSeason || movie.type === 'series') &&
          `${movie.title} ${movie.year ?? ""} ${movie.slug ?? ""}`
            .toLocaleLowerCase()
            .includes(needle),
        )
      : [];
  }, [catalog.items, query]);

  const continueWatching = useMemo(() => {
    const seen = new Set<string>();
    return progress.flatMap((item) => {
      const movie = catalog.available.items.find(
        (candidate) => candidate.id === item.movieId,
      );
      if (!movie || seen.has(movie.id)) return [];
      seen.add(movie.id);
      return [movie];
    });
  }, [catalog.available.items, progress]);
  const savedMovies = useMemo(() => {
    const byId = new Map(
      catalog.items.map((movie) => [watchlistId(movie), movie]),
    );
    const saved = savedTitles.map((movie) => byId.get(movie.id) ?? movie);
    const savedIds = new Set(saved.map((movie) => watchlistId(movie)));
    return [
      ...saved,
      ...catalog.items.filter(
        (movie) =>
          watchlist.includes(watchlistId(movie)) &&
          !savedIds.has(watchlistId(movie)),
      ),
    ];
  }, [catalog.items, savedTitles, watchlist]);
  const heroItems = useMemo(
    () => catalog.available.items.slice(0, 6),
    [catalog.available.items],
  );
  const safeHeroIndex = heroItems.length ? heroIndex % heroItems.length : 0;
  const hero = heroItems[safeHeroIndex] ?? null;

  const showHero = useCallback(
    (nextIndex: number, direction: 1 | -1) => {
      if (heroItems.length < 2 || nextIndex === safeHeroIndex) return;
      setPreviousHero(heroItems[safeHeroIndex] ?? null);
      setHeroDirection(direction);
      setHeroIndex((nextIndex + heroItems.length) % heroItems.length);
      if (heroTransitionTimer.current)
        window.clearTimeout(heroTransitionTimer.current);
      heroTransitionTimer.current = window.setTimeout(
        () => setPreviousHero(null),
        1_100,
      );
    },
    [heroItems, safeHeroIndex],
  );

  useEffect(() => {
    if (
      heroItems.length < 2 ||
      heroPaused ||
      view !== "home" ||
      selectedMovie ||
      releaseMovie ||
      playerMovie
    )
      return;
    const timer = window.setTimeout(
      () => showHero((safeHeroIndex + 1) % heroItems.length, 1),
      7_500,
    );
    return () => window.clearTimeout(timer);
  }, [
    heroItems.length,
    heroPaused,
    playerMovie,
    releaseMovie,
    safeHeroIndex,
    selectedMovie,
    showHero,
    view,
  ]);

  useEffect(
    () => () => {
      if (heroTransitionTimer.current)
        window.clearTimeout(heroTransitionTimer.current);
    },
    [],
  );

  const openMovie = async (movie: BrowseMovie) => {
    if (movie.type === 'series') {
      const tmdbId = movie.tmdbId?.replace(/^tv-series:/, '');
      if (tmdbId) {
        setSeriesId(knownSeries[tmdbId] ?? null);
        setSeriesTmdbId(tmdbId);
        setSeriesSeasonNumber(view === 'search' ? parseSeriesSeasonQuery(query)?.seasonNumber ?? null : null);
        setView('series');
      }
      return;
    }
    const version = ++detailVersion.current;
    const fallback = isSummary(movie)
      ? summaryDetail(movie)
      : discoveryDetail(movie);
    setSelectedMovie(fallback);
    setSelectedMovieId(isSummary(movie) ? movie.id : movie.movieId);
    const id = isSummary(movie) ? (movie.slug ?? movie.id) : movie.movieId;
    if (!client) return;
    setDetailLoading(true);
    try {
      if (!isSummary(movie)) {
        const metadata = await client.discoveryMovie(movie.tmdbId);
        if (detailVersion.current === version) {
          setSelectedMovie({
            ...fallback,
            ...metadata,
            id: metadata.movieId ?? fallback.id,
            slug: null,
            href: metadata.href ?? fallback.href,
            availability: fallback.availability,
            playback: null,
            video: null,
            durationSec: null,
            audio: [],
            subtitles: [],
          });
        }
        return;
      }
      if (!id) return;
      if (!movie.availability.playbackReady) {
        const [status, metadata, discovery] = await Promise.all([
          client.status(movie.id),
          client.metadataSearch(`${movie.title} ${movie.year ?? ""}`.trim()),
          movie.tmdbId
            ? client.discoveryMovie(movie.tmdbId.replace(/^movie:/, '')).catch(() => null)
            : Promise.resolve(null),
        ]);
        const exactMetadata = metadata.items.find(
          (item) => item.movieId === movie.id,
        );
        setSelectedMovie({
          ...summaryDetail(status),
          description:
            status.description ??
            exactMetadata?.description ??
            fallback.description,
          poster: status.poster ?? exactMetadata?.poster ?? fallback.poster,
          backdrop:
            status.backdrop ?? exactMetadata?.backdrop ?? fallback.backdrop,
          tmdbId: status.tmdbId ?? exactMetadata?.tmdbId ?? null,
          originalTitle: discovery?.originalTitle ?? status.originalTitle,
          tagline: discovery?.tagline ?? status.tagline,
          runtime: discovery?.runtime ?? status.runtime,
          genres: discovery?.genres ?? status.genres,
          countries: discovery?.countries ?? status.countries,
          actors: discovery?.actors ?? status.actors,
          directors: discovery?.directors ?? status.directors,
          writers: discovery?.writers ?? status.writers,
        });
      } else {
        const detail = await client.movie(id);
        if (detailVersion.current === version) setSelectedMovie(detail);
      }
    } catch {
      if (detailVersion.current === version) setSelectedMovie(fallback);
    } finally {
      if (detailVersion.current === version) setDetailLoading(false);
    }
  };

  const requestRelease = async (movie: DiscoveryMovie) => {
    if (!client) return;
    setReleaseMovie(movie);
    setReleases([]);
    setAcquiringReleaseKey(null);
    setAcquireProgress(null);
    setReleaseLoading(true);
    try {
      const response = await client.searchReleases(
        movie.releaseQuery ?? `${movie.title} ${movie.year ?? ""}`.trim(),
        movie.movieId ?? undefined,
        movie.tmdbId ?? undefined,
      );
      setReleases(response.releases);
    } catch {
      setToast(t("movieUnavailable"));
    } finally {
      setReleaseLoading(false);
    }
  };

  const requestSelected = () => {
    if (!selectedMovie) return;
    void requestRelease({
      tmdbId: selectedMovie.tmdbId ?? selectedMovie.id,
      title: selectedMovie.title,
      year: selectedMovie.year,
      description: selectedMovie.description ?? null,
      poster: selectedMovie.poster,
      backdrop: selectedMovie.backdrop ?? null,
      inLibrary: catalog.items.some((movie) => movie.id === selectedMovie.id),
      movieId: selectedMovieId,
      href: selectedMovie.href,
      type: "movie",
    });
  };

  const acquire = async (release: Release) => {
    if (!client || !releaseMovie) return;
    const requestedMovie = releaseMovie;
    const version = ++acquireVersion.current;
    const playWhenReady = isImmediatePlayerRelease(release);
    setAcquiringReleaseKey(releaseKey(release));
    setAcquireProgress(0);
    try {
      const releaseQuery =
        requestedMovie.releaseQuery ??
        `${requestedMovie.title} ${requestedMovie.year ?? ""}`.trim();
      const result = await client.acquire(
        releaseQuery,
        release,
        {
          title: requestedMovie.title,
          year: requestedMovie.year,
          tmdbId: requestedMovie.tmdbId,
          type: 'movie',
          description: requestedMovie.description,
          poster: requestedMovie.poster,
          backdrop: requestedMovie.backdrop,
        },
        releases.filter(
          (candidate) => releaseKey(candidate) !== releaseKey(release),
        ),
      );
      if (acquireVersion.current !== version) return;
      if (playWhenReady) {
        const playable = await waitForWebPlayback(
          client,
          result.movieId,
          () => acquireVersion.current !== version,
          (movie) =>
            setAcquireProgress(
              Math.max(0, Math.min(100, movie.availability.progress ?? 0)),
            ),
        );
        if (playable && acquireVersion.current === version) {
          setReleaseMovie(null);
          setSelectedMovie(null);
          setSelectedMovieId(null);
          setPlayerMovie(playable);
          await loadData();
          return;
        }
        if (acquireVersion.current !== version) return;
      }
      setReleaseMovie(null);
      setSelectedMovie(null);
      setSelectedMovieId(null);
      setView("library");
      setToast(t("movieAdded"));
      await loadData();
    } catch {
      setToast(t("addFailed"));
    } finally {
      if (acquireVersion.current === version) {
        setAcquiringReleaseKey(null);
        setAcquireProgress(null);
      }
    }
  };

  const changeLocale = (locale: ApiSettings["locale"]) => {
    if (locale === settings.locale) return;
    const next = { ...settings, locale };
    settingsStore.write(next);
    setSettings(next);
    setCatalog(emptyCatalog(locale));
    setTrending(emptyDiscovery(locale));
    setToast(translate(locale)("languageChanged"));
  };

  const openSearch = useCallback(() => {
    setView("search");
    window.requestAnimationFrame(() => searchInput.current?.focus());
  }, []);
  const navigate = useCallback(
    (nextView: View) => {
      if ((nextView === 'cybertube' && !features.cybertube) || (nextView === 'iptv' && !features.iptv)) return;
      if (nextView === "search") {
        if (view === "cybertube" || view === "iptv") setQuery("");
        openSearch();
      } else {
        const sectionWithOwnSearch = (candidate: View) =>
          candidate === "cybertube" || candidate === "iptv";
        if (sectionWithOwnSearch(nextView) || sectionWithOwnSearch(view))
          setQuery("");
        setView(nextView);
      }
    },
    [features, openSearch, view],
  );

  const openCybertube = useCallback((sourceId: string) => {
    setPlayerMovie(null);
    setCybertubeSource(sourceId);
  }, []);

  /**
   * CyberTube keeps its own resume points, keyed by YouTube video id rather
   * than by movie UUID, so it does not share the movie progress store.
   */
  const saveCybertubeProgress = useCallback(
    (sourceId: string, currentTime: number, duration: number) => {
      if (!client || duration <= 0) return;
      const completed = currentTime / duration >= 0.94;
      setCybertubeProgress((current) => {
        const rest = current.filter((item) => item.sourceId !== sourceId);
        if (completed) return rest;
        const existing = current.find((item) => item.sourceId === sourceId);
        return [
          {
            sourceId,
            videoId: existing?.videoId ?? null,
            title: existing?.title ?? "",
            channelTitle: existing?.channelTitle ?? null,
            thumbnail: existing?.thumbnail ?? null,
            currentTime,
            duration,
            updatedAt: Date.now(),
            completed: false,
          },
          ...rest,
        ];
      });
      progressSync.current = progressSync.current
        .catch(() => undefined)
        .then(() =>
          client.saveCybertubeProgress(sourceId, { currentTime, duration }),
        )
        .then(
          () => undefined,
          () => undefined,
        );
    },
    [client],
  );

  return (
    <I18nProvider locale={settings.locale}>
      <main className="cinema-shell">
        <div className="ambient-grid" />
        <Header
          searchInput={searchInput}
          enabled={auth.status === "authenticated"}
          authenticated={auth.status === "authenticated"}
          connected={connected}
          connection={connection}
          email={auth.session?.email ?? null}
          query={query}
          searching={view === "search" && searching}
          placeholder={
            view === "cybertube"
              ? t("searchCybertube")
              : view === "iptv"
                ? t("iptvChannels")
                : t("movieOrSeries")
          }
          onHome={() => {
            setQuery("");
            setView("home");
          }}
          onSearch={
            view === "cybertube" || view === "iptv"
              ? () => searchInput.current?.focus()
              : openSearch
          }
          onQueryChange={changeQuery}
          onAccount={() => {
            if (view === "cybertube" || view === "iptv") setQuery("");
            setView("account");
          }}
        />

        {auth.status === "loading" && (
          <HomeState loading connected={false} connection={t("connecting")} />
        )}
        {auth.status === "anonymous" && <AuthView />}

        {auth.status === "authenticated" && view === "home" && (
          <>
            {hero ? (
              <section
                className={`hero hero-direction-${heroDirection} ${heroPaused ? "hero-paused" : ""}`}
                aria-roledescription="carousel"
                aria-label={t("readyToWatch")}
                onMouseEnter={() => setHeroPaused(true)}
                onMouseLeave={() => setHeroPaused(false)}
                onFocus={() => setHeroPaused(true)}
                onBlur={(event) => {
                  if (
                    !event.currentTarget.contains(
                      event.relatedTarget as Node | null,
                    )
                  )
                    setHeroPaused(false);
                }}
              >
                {previousHero && (
                  <HeroScene
                    key={`previous-${previousHero.id}-${hero.id}`}
                    movie={previousHero}
                    previous
                  />
                )}
                <HeroScene key={`current-${hero.id}`} movie={hero}>
                  <div className="hero-actions">
                    <button
                      className="primary-action"
                      data-focusable="true"
                      onClick={() => {
                        if (hero.type === "series") void openMovie(hero);
                        else {
                          setPlayerMovie(summaryDetail(hero));
                        }
                      }}
                    >
                      ▶ {t("watch")}
                    </button>
                    <button
                      className="secondary-action"
                      data-focusable="true"
                      onClick={() => void openMovie(hero)}
                    >
                      {t("details")}
                    </button>
                  </div>
                </HeroScene>
                {heroItems.length > 1 && (
                  <div className="hero-navigation">
                    <button
                      data-focusable="true"
                      type="button"
                      onClick={() =>
                        showHero(
                          (safeHeroIndex - 1 + heroItems.length) %
                            heroItems.length,
                          -1,
                        )
                      }
                      aria-label="Previous"
                    >
                      ←
                    </button>
                    <div
                      className="hero-dots"
                      role="tablist"
                      aria-label={t("readyToWatch")}
                    >
                      {heroItems.map((item, index) => (
                        <button
                          key={item.id}
                          data-focusable="true"
                          type="button"
                          role="tab"
                          aria-selected={index === safeHeroIndex}
                          aria-label={item.title}
                          onClick={() =>
                            showHero(index, index > safeHeroIndex ? 1 : -1)
                          }
                        >
                          <i />
                        </button>
                      ))}
                    </div>
                    <button
                      data-focusable="true"
                      type="button"
                      onClick={() =>
                        showHero((safeHeroIndex + 1) % heroItems.length, 1)
                      }
                      aria-label="Next"
                    >
                      →
                    </button>
                  </div>
                )}
                <div className="hero-index">
                  <b>{String(safeHeroIndex + 1).padStart(2, "0")}</b>
                  <span> / {String(heroItems.length).padStart(2, "0")}</span>
                </div>
              </section>
            ) : (
              <HomeState
                loading={loading}
                connected={connected}
                connection={connection}
              />
            )}
            <ContinueRail
              movies={continueWatching}
              movieProgress={progress}
              cybertubeProgress={features.cybertube ? cybertubeProgress : []}
              onMovie={(movie) => void openMovie(movie)}
              onCybertube={(item) => openCybertube(item.sourceId)}
            />
            <MovieRail
              title={t("readyToWatch")}
              eyebrow="01"
              movies={catalog.available.items}
              progress={progress}
              onOpen={(movie) => void openMovie(movie)}
              action={
                <button
                  className="text-button"
                  data-focusable="true"
                  onClick={() => setView("library")}
                >
                  {t("fullLibrary")}
                </button>
              }
            />
            <MovieRail
              title={t("preparing")}
              eyebrow="ETA"
              movies={catalog.upcoming.items}
              onOpen={(movie) => void openMovie(movie)}
            />
            <MovieRail
              title={t("trending")}
              eyebrow="NOW"
              movies={trending.items}
              onOpen={(movie) => void openMovie(movie)}
            />
          </>
        )}

        {auth.status === "authenticated" && view === "search" && (
          <SearchPanel
            query={query}
            libraryResults={libraryResults}
            metadataResults={metadataResults}
            loading={searching}
            onOpen={(movie) => void openMovie(movie)}
          />
        )}
        {auth.status === "authenticated" && features.cybertube && view === "cybertube" && client && (
          <CyberTubeView client={client} query={query} onOpen={openCybertube} />
        )}
        {auth.status === 'authenticated' && view === 'series' && client && auth.session && (
          <SeriesView client={client} session={auth.session} initialTmdbId={seriesTmdbId} initialSeriesId={seriesId}
            initialSeasonNumber={seriesSeasonNumber} onUnreadChange={setSeriesUnread} onBackTargetChange={setSeriesHandlesBack}
            onInitialOpened={() => { setSeriesTmdbId(null); setSeriesId(null); setSeriesSeasonNumber(null); }} />
        )}
        {auth.status === "authenticated" && features.iptv && view === "iptv" && client && (
          <IptvView client={client} query={query} onOpen={setIptvChannel} />
        )}
        {auth.status === "authenticated" && view === "library" && (
          <LibraryView
            client={client}
            movies={catalog.items}
            saved={savedMovies}
            progress={progress}
            onOpen={(movie) => void openMovie(movie)}
            loading={loading}
          />
        )}
        {auth.status === "authenticated" && view === "account" && (
          <AccountView locale={settings.locale} onLocaleChange={changeLocale} client={client} audioLanguage={settings.preferredAudioLanguage ?? 'eng'} onAudioLanguageChange={code => {
            const next = { ...settings, preferredAudioLanguage: code };
            settingsStore.write(next); setSettings(next);
          }} />
        )}
        {auth.status === "authenticated" && (
          <BottomDock active={view} features={features} seriesUnread={seriesUnread} onChange={navigate} />
        )}

        {auth.session && selectedMovie && (
          <MovieDetailOverlay
            movie={selectedMovie}
            inLibrary={catalog.items.some(
              (movie) => movie.id === selectedMovie.id,
            )}
            inWatchlist={watchlist.includes(watchlistId(selectedMovie))}
            loading={detailLoading}
            onClose={() => {
              detailVersion.current += 1;
              setSelectedMovie(null);
              setSelectedMovieId(null);
              setDetailLoading(false);
            }}
            onPlay={() => {
              setPlayerMovie(selectedMovie);
            }}
            onToggleWatchlist={() => {
              const next = watchlistStore.toggle(
                watchlistId(selectedMovie),
                selectedMovie,
              );
              setWatchlist(next.ids);
              setSavedTitles(next.items);
            }}
            onRequest={requestSelected}
          />
        )}
        {auth.session && releaseMovie && (
          <ReleasePicker
            movie={releaseMovie}
            releases={releases}
            loading={releaseLoading}
            acquiringKey={acquiringReleaseKey}
            acquireProgress={acquireProgress}
            onClose={() => {
              acquireVersion.current += 1;
              setReleaseMovie(null);
              setAcquiringReleaseKey(null);
              setAcquireProgress(null);
            }}
            onAcquire={(release) => void acquire(release)}
          />
        )}
        {auth.session && playerMovie && (
          <PlayerOverlay
            key={playerMovie.id}
            movie={playerMovie}
            session={auth.session}
            resumeProgress={progress.find(
              (item) => item.movieId === playerMovie.id,
            )}
            onClose={() => {
              setPlayerMovie(null);
            }}
            onProgress={updateProgress}
            onClearProgress={clearProgress}
            onComplete={completeProgress}
          />
        )}
        {auth.session && client && features.cybertube && cybertubeSource && (
          <CyberTubeWatch
            key={cybertubeSource}
            client={client}
            sourceId={cybertubeSource}
            resume={cybertubeProgress.find(
              (item) => item.sourceId === cybertubeSource,
            )}
            onOpen={openCybertube}
            onClose={() => setCybertubeSource(null)}
            onProgress={saveCybertubeProgress}
          />
        )}
        {auth.session && client && features.iptv && iptvChannel && (
          <IptvWatch
            key={iptvChannel}
            client={client}
            channelId={iptvChannel}
            onOpen={setIptvChannel}
            onClose={() => setIptvChannel(null)}
          />
        )}
        {toast && (
          <div className="toast" role="status">
            {toast}
          </div>
        )}
      </main>
    </I18nProvider>
  );
}

function HeroScene({
  movie,
  previous = false,
  children,
}: {
  movie: MovieSummary;
  previous?: boolean;
  children?: ReactNode;
}) {
  const { locale, t } = useI18n();
  const style = movie.backdrop
    ? { backgroundImage: `url("${movie.backdrop}")` }
    : undefined;
  return (
    <div
      className={`hero-scene ${previous ? "hero-scene-previous" : "hero-scene-current"}`}
      aria-hidden={previous || undefined}
    >
      <div className="hero-backdrop" style={style} />
      <div className="hero-light" />
      <div className="hero-noise" />
      <div className="hero-content">
        <h1>{movie.title}</h1>
        <div className="hero-meta">
          {movie.year && <span>{movie.year}</span>}
          <span>
            {formatDuration(
              movie.durationSec ?? (movie.runtime ? movie.runtime * 60 : null),
              locale,
            )}
          </span>
          {movie.type === "series" && <span>{t("series")}</span>}
        </div>
        {movie.description && <p>{movie.description}</p>}
        {children}
      </div>
    </div>
  );
}

function Header({
  searchInput,
  enabled,
  authenticated,
  connected,
  connection,
  email,
  query,
  searching,
  placeholder,
  onHome,
  onSearch,
  onQueryChange,
  onAccount,
}: {
  searchInput: RefObject<HTMLInputElement | null>;
  enabled: boolean;
  authenticated: boolean;
  connected: boolean;
  connection: string;
  email: string | null;
  query: string;
  searching: boolean;
  placeholder: string;
  onHome: () => void;
  onSearch: () => void;
  onQueryChange: (query: string) => void;
  onAccount: () => void;
}) {
  const { t } = useI18n();
  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if (
        !enabled ||
        event.key !== "/" ||
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      )
        return;
      event.preventDefault();
      onSearch();
      searchInput.current?.focus();
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, [enabled, onSearch, searchInput]);

  return (
    <header className="topbar glass-panel">
      <button
        className="brand"
        data-focusable="true"
        onClick={onHome}
        aria-label={`Cyber TV — ${t("home")}`}
      >
        <span className="brand-mark">
          <img src="/cd-logo-mini.svg" alt="" />
        </span>
        <span>
          CYBER<span>TV</span>
        </span>
      </button>
      <div className={`search-pill ${enabled ? "" : "disabled"}`}>
        <i>⌕</i>
        <input
          ref={searchInput}
          data-focusable={enabled ? "true" : undefined}
          type="search"
          value={query}
          onFocus={onSearch}
          onChange={(event) => {
            onQueryChange(event.target.value);
            onSearch();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          placeholder={placeholder}
          disabled={!enabled}
          aria-label={placeholder}
        />
        {searching && <span className="tiny-loader" />}
        {query && (
          <button
            data-focusable="true"
            type="button"
            onClick={() => {
              onQueryChange("");
              searchInput.current?.focus();
            }}
            aria-label={t("clearSearch")}
          >
            ×
          </button>
        )}
        <kbd>/</kbd>
      </div>
      <div className="top-actions">
        {authenticated && (
          <span
            className={`connection-label ${connected ? "" : "offline"}`}
            title={connection}
          >
            <i />
            {connected ? t("live") : t("offline")}
          </span>
        )}
        <button
          className="avatar"
          data-focusable="true"
          type="button"
          aria-label={t("account")}
          onClick={onAccount}
        >
          {authenticated ? email?.slice(0, 1).toUpperCase() || "A" : "A"}
        </button>
      </div>
    </header>
  );
}

function BottomDock({
  active,
  features,
  seriesUnread,
  onChange,
}: {
  active: View;
  features: FrontendFeatures;
  seriesUnread: number;
  onChange: (view: View) => void;
}) {
  const { t } = useI18n();
  const items: Array<{ view: View; icon: string; label: string }> = [
    { view: "home", icon: "⌂", label: t("home") },
    { view: "search", icon: "⌕", label: t("search") },
    { view: 'series', icon: '▤', label: t('series') },
    ...(features.cybertube ? [{ view: "cybertube" as const, icon: "▶", label: t("cybertube") }] : []),
    ...(features.iptv ? [{ view: "iptv" as const, icon: "📡", label: t("iptv") }] : []),
    { view: "library", icon: "▦", label: t("library") },
  ];
  return (
    <nav className="dock glass-panel" aria-label={t("mainNavigation")}>
      {items.map((item) => (
        <span key={item.view} className="dock-wrap">
          <button
            className={`dock-item ${active === item.view ? "active" : ""}`}
            data-focusable="true"
            onClick={() => onChange(item.view)}
          >
            <i>{item.icon}</i>
            <span>{item.label}</span>
            {item.view === 'series' && seriesUnread > 0 && (
              <b className="dock-badge" aria-label={t('seriesUnread', { count: seriesUnread })}>{seriesUnread > 9 ? '9+' : seriesUnread}</b>
            )}
          </button>
        </span>
      ))}
    </nav>
  );
}

function ContinueRail({
  movies,
  movieProgress,
  cybertubeProgress,
  onMovie,
  onCybertube,
}: {
  movies: MovieSummary[];
  movieProgress: WatchProgress[];
  cybertubeProgress: CybertubeProgress[];
  onMovie: (movie: MovieSummary) => void;
  onCybertube: (item: CybertubeProgress) => void;
}) {
  const { t } = useI18n();
  const movieEntries = movies.map((movie) => ({
    kind: "movie" as const,
    movie,
    progress: movieProgress.find((item) => item.movieId === movie.id),
  }));
  const cyberEntries = cybertubeProgress.map((item) => ({
    kind: "cybertube" as const,
    movie: cybertubeProgressMovie(item),
    progress: cybertubeWatchProgress(item),
    item,
  }));
  const entries = [...movieEntries, ...cyberEntries].sort(
    (a, b) => (b.progress?.updatedAt ?? 0) - (a.progress?.updatedAt ?? 0),
  );
  if (!entries.length) return null;
  return (
    <section className="catalog-rail">
      <div className="section-heading">
        <div>
          <h2>{t("continueWatching")}</h2>
        </div>
      </div>
      <div className="catalog-row">
        {entries.map((entry, index) => (
          <MovieCard
            key={entry.movie.id}
            movie={entry.movie}
            progress={entry.progress}
            index={index}
            onOpen={() =>
              entry.kind === "cybertube"
                ? onCybertube(entry.item)
                : onMovie(entry.movie)
            }
          />
        ))}
      </div>
    </section>
  );
}

function HomeState({
  loading,
  connected,
  connection,
}: {
  loading: boolean;
  connected: boolean;
  connection: string;
}) {
  const { t } = useI18n();
  return (
    <section className="home-state glass-panel">
      <b>
        {loading
          ? t("loading")
          : connected
            ? t("libraryEmpty")
            : t("temporarilyUnavailable")}
      </b>
      <span>{connection}</span>
    </section>
  );
}

function LibraryView({
  client,
  movies: allMovies,
  saved,
  progress,
  onOpen,
  loading,
}: {
  client: DistributionClient | null;
  movies: MovieSummary[];
  saved: MovieSummary[];
  progress: WatchProgress[];
  onOpen: (movie: MovieSummary) => void;
  loading: boolean;
}) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<CatalogFilterSelection>(EMPTY_CATALOG_FILTER);
  const filtering = catalogFilterActive(filter);
  // The whole catalog is loaded here: the server's rule is applied to the cards' facts.
  const movies = useMemo(
    () => (filtering ? allMovies.filter((movie) => matchesCatalogFilter(movie, filter)) : allMovies),
    [allMovies, filter, filtering],
  );
  return (
    <section className="library-view">
      {loading && <div className="loading-line" />}
      {saved.length > 0 && (
        <MovieRail
          title={t("watchLater")}
          eyebrow="SAVE"
          movies={saved}
          progress={progress}
          onOpen={(movie) => onOpen(movie as MovieSummary)}
        />
      )}
      {(movies.length > 0 || filtering) && (
        <div
          className={`section-heading library-heading ${saved.length ? "after-saved" : ""}`}
        >
          <div>
            <span>{t("library").toUpperCase()}</span>
            <h2>{t("allMovies")}</h2>
          </div>
        </div>
      )}
      {allMovies.length > 0 && (
        <CatalogFilters client={client} kind="movie" selection={filter} onChange={setFilter} />
      )}
      <div className="library-grid">
        {movies.map((movie, index) => (
          <MovieCard
            key={movie.id}
            movie={movie}
            index={index}
            progress={progress.find((item) => item.movieId === movie.id)}
            onOpen={(item) => onOpen(item as MovieSummary)}
          />
        ))}
      </div>
      {!movies.length && filtering && (
        <div className="empty-state">
          <b>{t("noFilterMatches")}</b>
        </div>
      )}
      {!movies.length && !filtering && (
        <div className="empty-state">
          <b>{t("libraryEmpty")}</b>
          <span>{t("useSearchToAdd")}</span>
        </div>
      )}
    </section>
  );
}
